import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Shield, CheckCircle, XCircle, Loader2, 
  CreditCard, Camera, FileText, ArrowRight, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { identityApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const IdentityVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);

  // Check if returning from Stripe
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  useEffect(() => {
    // If returning from Stripe verification
    if (sessionId) {
      handleReturnFromStripe(sessionId);
    }
  }, [sessionId]);

  const checkVerificationStatus = async () => {
    try {
      const response = await identityApi.getStatus();
      setVerificationStatus(response.data);
    } catch (error) {
      console.error('Error checking status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleReturnFromStripe = async (sid) => {
    setLoading(true);
    try {
      const response = await identityApi.checkSession(sid);
      if (response.data.verified) {
        toast.success('Identité vérifiée avec succès!');
        if (refreshUser) refreshUser();
      } else if (response.data.status === 'requires_input') {
        toast.error('La vérification a échoué. Veuillez réessayer.');
      } else {
        toast.info('Vérification en cours de traitement...');
      }
      checkVerificationStatus();
    } catch (error) {
      toast.error('Erreur lors de la vérification du statut');
    } finally {
      setLoading(false);
      // Clear URL params
      navigate('/dashboard/verification', { replace: true });
    }
  };

  const startVerification = async () => {
    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/dashboard/verification?session_id={CHECKOUT_SESSION_ID}`;
      const response = await identityApi.createSession(returnUrl);
      
      // Redirect to Stripe Identity
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la session');
      setLoading(false);
    }
  };

  const getStatusBadge = () => {
    if (!verificationStatus) return null;
    
    // Use identity_verified as source of truth
    if (verificationStatus.identity_verified) {
      return (
        <div className="flex items-center gap-2 text-green-600 bg-green-50 px-4 py-2 rounded-full">
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Identité vérifiée</span>
        </div>
      );
    }
    
    const status = verificationStatus.verification_status;
    
    switch (status) {
      case 'pending':
        return (
          <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-full">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="font-medium">Vérification en cours</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center gap-2 text-red-600 bg-red-50 px-4 py-2 rounded-full">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">Vérification échouée</span>
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-2 text-slate-600 bg-slate-100 px-4 py-2 rounded-full">
            <AlertCircle className="w-5 h-5" />
            <span className="font-medium">Non vérifié</span>
          </div>
        );
    }
  };

  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  const isVerified = verificationStatus?.identity_verified;

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="verification-page">
      <div className="text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-orange-600" />
        </div>
        <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-900 uppercase tracking-tight">
          Vérification d&apos;identité
        </h1>
        <p className="text-slate-600 mt-2">
          Vérifiez votre identité pour renforcer la confiance sur la plateforme
        </p>
        <div className="mt-4">
          {getStatusBadge()}
        </div>
      </div>

      {isVerified ? (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-green-700 mb-2">Identité vérifiée!</h2>
            <p className="text-green-600">
              Votre identité a été vérifiée avec succès. Les autres utilisateurs peuvent voir que vous êtes un membre vérifié.
            </p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="mt-6 bg-green-600 hover:bg-green-700"
            >
              Retour au tableau de bord
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Benefits */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Pourquoi vérifier votre identité?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Confiance renforcée</h3>
                  <p className="text-sm text-slate-600">Les utilisateurs vérifiés inspirent plus confiance</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Badge vérifié</h3>
                  <p className="text-sm text-slate-600">Un badge s&apos;affiche sur votre profil</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-medium text-slate-900">Plus d&apos;opportunités</h3>
                  <p className="text-sm text-slate-600">Accédez à plus de missions et de clients</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Process */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Comment ça marche?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 md:gap-8">
                <div className="flex-1 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-slate-600" />
                  </div>
                  <h3 className="font-medium text-slate-900 mb-1">1. Document</h3>
                  <p className="text-sm text-slate-500">Photographiez votre pièce d&apos;identité</p>
                </div>
                <div className="hidden md:flex items-center">
                  <ArrowRight className="w-5 h-5 text-slate-300" />
                </div>
                <div className="flex-1 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Camera className="w-6 h-6 text-slate-600" />
                  </div>
                  <h3 className="font-medium text-slate-900 mb-1">2. Selfie</h3>
                  <p className="text-sm text-slate-500">Prenez un selfie pour comparaison</p>
                </div>
                <div className="hidden md:flex items-center">
                  <ArrowRight className="w-5 h-5 text-slate-300" />
                </div>
                <div className="flex-1 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-medium text-slate-900 mb-1">3. Vérifié</h3>
                  <p className="text-sm text-slate-500">Vérification instantanée</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents accepted */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Documents acceptés</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <span className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm">
                  🪪 Carte d&apos;identité
                </span>
                <span className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm">
                  🛂 Passeport
                </span>
                <span className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm">
                  🚗 Permis de conduire
                </span>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <Card className="border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
            <CardContent className="p-6 text-center">
              <p className="text-slate-600 mb-4">
                La vérification prend environ <strong>2 minutes</strong> et est sécurisée par Stripe.
              </p>
              <Button
                onClick={startVerification}
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide px-8 py-3"
                data-testid="start-verification-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Chargement...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Vérifier mon identité
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                Vos données sont traitées de manière sécurisée et confidentielle
              </p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default IdentityVerificationPage;
